import React from "react";

/**
 * 14029 — Compact Risk Summary cell for the My VQQs / VQQ Requests /
 * vendor-side / My Documents / Library Requests grids.
 *
 * Layout (per AC):
 *   [RAG pill]  82%
 *   [crit][high][med][low]   — only non-zero severities shown, in this order
 *
 * Renders `--` (greyed) when no published summary applies to this row.
 *
 * The engine's `overall_tier` is mapped to a RAG band (Red / Yellow / Green)
 * here on the FE so changes to the tier vocabulary don't require a BE deploy.
 */

const SEVERITY_CHIP_CLASSES = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-yellow-400",
  low: "bg-green-500",
};

const SEVERITY_CHIP_LABEL = {
  critical: "C",
  high: "H",
  medium: "M",
  low: "L",
};

const SEVERITY_CHIP_TITLE = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

const RAG_BAND_CLASSES = {
  Red: "bg-red-100 text-red-800 border-red-200",
  Yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Green: "bg-green-100 text-green-800 border-green-200",
};

/**
 * Maps the engine's `overall_tier` to a Red / Yellow / Green band per AC.
 * Critical + High → Red. Medium → Yellow. Low + Compliant → Green.
 * Returns null for anything we don't recognise (cell falls back to "--").
 */
export function tierToRagBand(tier) {
  const t = String(tier || "").trim().toLowerCase();
  if (!t) return null;
  if (t === "critical" || t === "high") return "Red";
  if (t === "medium") return "Yellow";
  if (t === "low" || t === "compliant") return "Green";
  return null;
}

/**
 * 14029 — Per AC the export reads `<RAG> (<percent>%)` when present, blank
 * when absent. Shared with the grid → CSV/Excel exporters so the displayed
 * cell and the exported cell always agree.
 */
export function formatRiskSummaryForExport(summary) {
  if (!summary) return "";
  const band = tierToRagBand(summary.overall_tier);
  if (!band) return "";
  const score = Number(summary.overall_score);
  if (!Number.isFinite(score)) return band;
  return `${band} (${Math.round(score)}%)`;
}

const EmptyCell = () => <span className="text-gray-400">--</span>;

const FindingChip = ({ severity, count }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700"
    title={`${SEVERITY_CHIP_TITLE[severity]}: ${count}`}
  >
    <span
      className={`inline-block h-1.5 w-1.5 rounded-full ${SEVERITY_CHIP_CLASSES[severity]}`}
      aria-hidden="true"
    />
    <span className="leading-none">
      {SEVERITY_CHIP_LABEL[severity]} {count}
    </span>
  </span>
);

export default function RiskSummaryCell({ summary }) {
  if (!summary) return <EmptyCell />;
  const band = tierToRagBand(summary.overall_tier);
  if (!band) return <EmptyCell />;
  const score = Number(summary.overall_score);
  const scoreDisplay = Number.isFinite(score) ? `${Math.round(score)}%` : "";
  const chips = [
    { key: "critical", count: Number(summary.finding_count_critical) || 0 },
    { key: "high", count: Number(summary.finding_count_high) || 0 },
    { key: "medium", count: Number(summary.finding_count_medium) || 0 },
    { key: "low", count: Number(summary.finding_count_low) || 0 },
  ].filter((c) => c.count > 0);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${RAG_BAND_CLASSES[band]}`}
        >
          {band}
        </span>
        {scoreDisplay && (
          <span className="text-xs font-semibold text-gray-700">
            {scoreDisplay}
          </span>
        )}
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {chips.map((c) => (
            <FindingChip key={c.key} severity={c.key} count={c.count} />
          ))}
        </div>
      )}
    </div>
  );
}
